import {utils} from '@mc/redux';
import {Store} from 'redux';
import {FrameReducer, FrameState} from '@mc/react-frame';
import {GroupReducer, isReactNative, ReducerState} from '../utils';
import ConfigReducer, {ConfigState} from './ConfigReducer';
import {ConfigProps, getConfigProps} from '../components/props';

/**
 * Stav mapy.
 */
export interface MapState extends ReducerState<MapState> {
    /**
     * Stav frame pro vykreslení webové stránky mapy.
     */
    frame: FrameState;

    /**
     * Stav konfigurace mapy.
     */
    config: ConfigState;
}

/**
 * Factory výchozího stavu store pro mapové reducery.
 */
const MapReducers = {
    /**
     * Vrátí výchozí stav store bez konfigurace mapy.
     *
     * @returns Stav store.
     */
    base(): FrameState {
        return FrameReducer();
    },

    /**
     * Vrátí výchozí stav store s konfigurací mapy.
     *
     * @param defaultConfig - Výchozí konfigurace mapy.
     * @returns Stav store.
     */
    full(defaultConfig: ConfigProps = {}): MapState {
        return GroupReducer({
            frame: FrameReducer(),
            config: {...ConfigReducer(), ...defaultConfig},
        });
    },

    /**
     * Vrátí výchozí stav store s konfigurací mapy načtenou z local storage.
     *
     * @param localStorageKey - Klíč do local storage.
     * @param fixedConfig - Fixní konfigurace mapy. Přepíše načtené hodnoty ze storage.
     * @param defaultConfig - Výchozí konfigurace mapy. Použije se pokud nebylo
     *                        pod daným klíčem v local storage nic uloženo.
     * @returns Stav store.
     */
    fromLocalStorage(
        localStorageKey: string,
        fixedConfig: ConfigProps = {},
        defaultConfig: ConfigProps = {},
    ): MapState {
        if (isReactNative) {
            throw new Error('Operation not supported in React Native');
        }

        let storedConfig;

        try {
            const rawStoredConfig = localStorage.getItem(localStorageKey);
            if (rawStoredConfig) {
                storedConfig = JSON.parse(rawStoredConfig) as ConfigState;
            }
        } catch (error) {
            console.error(
                '[MapReducers.fromLocalStorage] Failed to load config state from local storage with key "' +
                    localStorageKey +
                    '"',
                error,
            );
        }

        if (!storedConfig) {
            storedConfig = defaultConfig;
        }

        return MapReducers.full({
            ...storedConfig,
            ...fixedConfig,
            localStorageKey,
        });
    },

    /**
     * Zajistí automatickou persistenci konfigurace mapy ve store do local storage při změně.
     *
     * @param store - Store.
     * @param areas - Pole oblastí ve store, které perzistovat.
     */
    persistInLocalStorage(store: Store, areas: string[]): void {
        if (isReactNative) {
            throw new Error('Operation not supported in React Native');
        }
        utils.subscribeToAreaChanges(store, areas, (newState: any, oldState, area) => {
            if (newState.config && newState.config.localStorageKey) {
                const {localStorageKey} = newState.config;
                const storedConfig = getConfigProps(newState.config);
                localStorage.setItem(localStorageKey, JSON.stringify(storedConfig));
            } else {
                console.warn(
                    '[MapReducers.persistInLocalStorage] Unable to persist config state from store area "' +
                        area +
                        '" to local storage. Store area does not contain "config.localStorageKey" property.',
                );
            }
        });
    },
};

export default MapReducers as Readonly<typeof MapReducers>;
